import type { RpcTransport } from 'capnweb';

export type RpcSocketTransport = RpcTransport & {
    onMessage: (message: string) => void;
    onError: (error: Error) => void;
};

/**
 * Utility to create a transport over something a custom socket where the message can be sent the way we want.
 * https://github.com/cloudflare/capnweb/blob/7c4207a4830c5cb50d1c1590cfd4fa22cf50b129/src/websocket.ts#L41
 */
export function createSocketTransport(socket: {
    send: (message: string) => void;
}): RpcSocketTransport {
    let receiveResolver: ((message: string) => void) | undefined;
    let receiveRejecter: ((error: Error) => void) | undefined;
    const receiveQueue: string[] = [];
    let error: Error | undefined;

    return {
        async send(message) {
            socket.send(message);
        },
        async receive() {
            if (receiveQueue.length > 0) {
                return receiveQueue.shift()!;
            } else if (error) {
                throw error;
            } else {
                return new Promise<string>((resolve, reject) => {
                    receiveResolver = resolve;
                    receiveRejecter = reject;
                });
            }
        },
        abort(): void {},
        onMessage(message) {
            if (error) {
                // The transport already failed: frames from the dead connection are dropped.
                return;
            }

            if (receiveResolver) {
                receiveResolver(message);
                receiveResolver = undefined;
                receiveRejecter = undefined;
            } else {
                receiveQueue.push(message);
            }
        },
        onError(failure) {
            if (error) {
                return;
            }

            // Fail the pending and future receives so the RPC session aborts and rejects
            // every pending call, instead of leaving them hanging forever on a connection
            // that will never answer.
            error = failure;
            if (receiveRejecter) {
                receiveRejecter(failure);
                receiveResolver = undefined;
                receiveRejecter = undefined;
            }
        },
    };
}
